<!-- /pages/whats-new.vue -->
<template>
    <div class="bg-slate-950">
        
        <div class="w-full relative mx-auto max-w-[1120px]">
    
            <div class="min-h-screen text-slate-50 ">
              <div class="mx-auto flex max-w-5xl flex-col gap-12 px-4 py-16 lg:py-20">
                <!-- Hero -->
                <section class="space-y-6">
                  <div class="inline-flex items-center gap-2 rounded-full bg-slate-900/80 px-3 py-1 text-xs font-medium text-slate-300 ring-1 ring-slate-700/80">
                    <span class="h-1.5 w-1.5 rounded-full bg-emerald-400" />
                    <span>Smoxy Community Update · December 14</span>
                  </div>
          
                  <div class="space-y-4">
                    <h1 class="text-3xl font-semibold tracking-tight sm:text-4xl lg:text-5xl">
                      Human-centered support<br class="hidden sm:block" />
                      for quitting smoking
                    </h1>
                    <p class="max-w-2xl text-base text-slate-300 sm:text-lg">
                      Smoxy introduces a safe in-app community and shareable profiles to make the
                      quit journey less isolating and more empowering — built on a truly
                      human-centered approach.
                    </p>
                  </div>
          
                  <div class="flex flex-wrap items-center gap-3">
                    <Button class="rounded-full">
                        <a 
                            href="smoxy://live"
                            @click.prevent="openSmoxy"
                    >
                        Open in Smoxy App
                    </a>
                    </Button>
                    <Button variant="outline" class="rounded-full border-slate-600 bg-transparent text-slate-100 hover:bg-slate-900">
                        <a href="https://smoxy.app" target="_blank" rel="noopener noreferrer">
                            Learn more about Smoxy
                        </a>
                    </Button>
                  </div>
          
                  <!-- Hero Screenshot Placeholder -->
                  <Card class="mt-4 overflow-hidden border-slate-800 bg-slate-900/40">
                    <CardContent class="p-0">
                      <div class="flex aspect-[16/9] items-center justify-center bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-sm text-slate-400">
                        <img
                          src="/app_community.jpeg"
                          alt="Smoxy in-app community feed"
                          width="1365"
                          height="768"
                          decoding="async"
                          fetchpriority="high"
                        />
                      </div>
                    </CardContent>
                  </Card>
                </section>
          
                <!-- Community Section -->
                <section class="grid gap-8 lg:grid-cols-[minmax(0,1.1fr)_minmax(0,0.9fr)] lg:items-center">
                  <div class="space-y-4">
                    <h2 class="text-xl font-semibold tracking-tight sm:text-2xl">
                      A more connected smoke-free journey
                    </h2>
                    <p class="text-slate-300">
                      With this update, Smoxy introduces a safe in-app community designed to make
                      the quit journey less isolating and more empowering. People can share their
                      experiences, encourage each other, and celebrate progress — all in a
                      supportive environment built around empathy and positive reinforcement.
                    </p>
                    <ul class="mt-3 space-y-2 text-sm text-slate-300">
                      <li class="flex gap-2">
                        <span class="mt-1 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-emerald-400" />
                        <span>Share milestones and daily wins with others on the same path.</span>
                      </li>
                      <li class="flex gap-2">
                        <span class="mt-1 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-emerald-400" />
                        <span>Exchange tips, coping ideas, and emotional support.</span>
                      </li>
                      <li class="flex gap-2">
                        <span class="mt-1 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-emerald-400" />
                        <span>Feel less alone during tough moments and cravings.</span>
                      </li>
                    </ul>
                  </div>
          
                  <!-- Community Screenshot Placeholder -->
                  <img
                    src="/live.png"
                    alt="Smoxy community screen in the app"
                    width="810"
                    height="1650"
                    loading="lazy"
                    decoding="async"
                    class="lg:order-1 sm:max-w-72"
                  />
                </section>
          
                <!-- Profile Sharing Section -->
                <section class="grid gap-9 lg:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)] lg:items-center">
                  <!-- Profile Sharing Screenshot Placeholder -->
                

                  <div class="lg:order-1 flex items-center justify-center rounded-2xl overflow-clip md:m-10 bg-slate-900/60 text-xs text-slate-400">
                    <img
                      src="/share_profile_screen.jpeg"
                      class="object-cover"
                      alt="Sharing a Smoxy profile for support"
                      width="768"
                      height="1365"
                      loading="lazy"
                      decoding="async"
                    />
                  </div>
          
                  <div class="order-1 space-y-4 lg:order-2">
                    <h2 class="text-xl font-semibold tracking-tight sm:text-2xl">
                      Share your profile. Inspire support.
                    </h2>
                    <p class="text-slate-300">
                      A new shareable profile makes it easy for users to involve friends, family,
                      or followers in their smoke-free journey. By sharing a personal link,
                      supporters can send encouragement and help strengthen motivation during
                      challenging moments.
                    </p>
                    <ul class="mt-3 space-y-2 text-sm text-slate-300">
                      <li class="flex gap-2">
                        <span class="mt-1 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-emerald-400" />
                        <span>Personal profile links for Instagram, TikTok, WhatsApp, and more.</span>
                      </li>
                      <li class="flex gap-2">
                        <span class="mt-1 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-emerald-400" />
                        <span>Invite close friends and family to cheer on the quit journey.</span>
                      </li>
                      <li class="flex gap-2">
                        <span class="mt-1 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-emerald-400" />
                        <span>Turn quitting from a solo challenge into a shared experience.</span>
                      </li>
                    </ul>
                  </div>
                </section>
          
                <!-- Superpowers Section -->
                <section class="grid gap-8 lg:grid-cols-[minmax(0,1.1fr)_minmax(0,0.9fr)] lg:items-center">
                  <div class="space-y-4">
                    <h2 class="text-xl font-semibold tracking-tight sm:text-2xl">
                      Strength in moments that matter
                    </h2>
                    <p class="text-slate-300">
                      Quitting smoking is especially difficult during high-craving moments.
                      Smoxy takes a human-centered approach supported by behavioral science to
                      guide people through these spikes.
                    </p>
                    <p class="text-slate-300">
                      While not part of this update, Smoxy’s unique, science-based
                      <span class="font-semibold text-emerald-300"> Superpowers</span>
                      remain a core element that sets the app apart. These real-time coping
                      strategies help users navigate cravings with clarity, confidence, and
                      actionable guidance.
                    </p>
                  </div>
          
                  <!-- Superpowers Screenshot Placeholder -->
                   <!--
                  <Card class="bg-primary-500 rounded-2xl border-none overflow-clip items-center relative">
                    <div class="h-96 w-96 absolute center-stack">

                        <client-only>
                            <Vue3Lottie
                                ref="controller"
                                alt="Hand with device through portal"
                               
                                :animation-data="PortalJSON"
                                :loop="false"
                                @on-complete="onComplete"
                                />
                        </client-only>
                    </div>
                </Card>
                -->
               
                  <Card class="bg-primary-500 rounded-2xl border-none overflow-clip">
                    <div class="relative h-96">
                    <div class="h-96 w-60 absolute center-stack">
                       
                        <!--
                            <client-only> 
                                <Vue3Lottie
                                
                                alt="Superpower star burst animation"
                                class="relative scale-[2.5] translate-y-3"       
                                :loop="true"
                                :auto-play="true"
                                :animationData="SuperPowerAniamtion" 
                                />
                            </client-only> 
                            -->
                            <client-only>
                              <Vue3Lottie
                                  ref="controller"
                                  alt="Hand with device through portal"
                                
                                  :animation-data="PortalJSON"
                                  :loop="false"
                                  @on-complete="onComplete"
                                  />
                            </client-only>

                    </div>
                   <!-- 
                    <img alt="Smoxy superpower star"
                        src="@/assets/icon_superpower.svg"
                        class="w-38 h-38 absolute center-stack"
                        width="240" height="240"
                        />  
                        -->
                </div>
                  </Card>
               
                


                </section>
          
                <!-- Why it matters + About -->
                <section class="grid gap-8 lg:grid-cols-2">
                  <Card class="border-slate-800 bg-slate-900/40">
                    <CardHeader>
                      <CardTitle class="text-base font-semibold text-slate-100">
                        Why this update matters
                      </CardTitle>
                    </CardHeader>
                    <CardContent class="space-y-2 text-sm text-slate-300">
                      <p>
                        This update strengthens Smoxy’s mission to make quitting feel achievable
                        and less lonely.
                      </p>
                      <ul class="space-y-2">
                        <li class="flex gap-2">
                          <span class="mt-1 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-emerald-400" />
                          <span>Builds meaningful connections through community support.</span>
                        </li>
                        <li class="flex gap-2">
                          <span class="mt-1 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-emerald-400" />
                          <span>Turns quitting into a shared, motivating experience.</span>
                        </li>
                        <li class="flex gap-2">
                          <span class="mt-1 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-emerald-400" />
                          <span>Works hand in hand with Smoxy’s real-time craving guidance.</span>
                        </li>
                      </ul>
                    </CardContent>
                  </Card>
          
                  <Card class="border-slate-800 bg-slate-900/40">
                    <CardHeader>
                      <CardTitle class="text-base font-semibold text-slate-100">
                        Available December 14
                      </CardTitle>
                    </CardHeader>
                    <CardContent class="space-y-3 text-sm text-slate-300">
                      <p>
                        The update is fully production-ready and will be released on
                        <span class="font-medium text-slate-50"> December 14</span>. Features
                        can be enabled progressively via remote configuration to ensure a smooth
                        rollout.
                      </p>
                      <p>
                        Smoxy is built by a small, independent team focused on accessibility,
                        inclusivity, and empathy. The goal: help people quit smoking sustainably
                        by surrounding them with support, strength, and meaningful connections.
                      </p>
                    </CardContent>
                  </Card>
                </section>
          
                <!-- Footer CTA -->
                <section class="border-t border-slate-800 pt-8 pb-10">
                  <div class="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                    <div class="space-y-1">
                      <p class="text-sm font-medium text-slate-100">
                        Ready for a more supported quit journey?
                      </p>
                      <p class="text-sm text-slate-400">
                        Smoxy combines human-centered design, community, and real-time guidance
                        to make quitting feel possible — together.
                      </p>
                    </div>
                    <div class="flex gap-3">
                   
                      <Button asChild class="rounded-full">
                        <a href="https://apps.apple.com/app/apple-store/id1511487101?pt=117785216&ct=applefeat&mt=8" target="_blank" rel="noopener noreferrer">
                            Download Smoxy
                        </a>
                      </Button>
                   
                        <Button asChild  variant="outline" class="rounded-full border-slate-600 bg-transparent text-slate-100 hover:bg-slate-900">
                        <a href="mailto:info@offlinefirst.net?subject=Smoxy%20Community%20Update&body=Hi%20Smoxy%20Team,%0D%0A%0D%0AI'd%20like%20to%20get%20in%20touch%20regarding%20Smoxy.%0D%0A%0D%0ABest%20regards,%0D%0A">
                            Contact the team
                        </a>
                        </Button>

                       
                    </div>
                  </div>
                </section>
              </div>
            </div>
        </div>
    </div>
  </template>
  
<script setup lang="ts">
  import { Button } from '@/components/ui/button'
  import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
  
  //import SuperPowerAniamtion from "@/assets/burst.json";
  import PortalJSON from "@/assets/portal.json";
  const route = useRoute()
  const config = useRuntimeConfig()
  const controller = ref(null)
  const onComplete = () => {
    (controller.value as any).goToAndPlay(58, true);
  }

  const title = 'What’s New in Smoxy - Community Update'
  const description = 'Discover the latest Smoxy update with a new in-app community, shareable profiles, and more human-centered quit-smoking support.'
  const canonicalUrl = `${config.public.hostname}${route.path}`
  const shareImage = `${config.public.hostname}/app_community.jpeg`

  useSeoMeta({
    title,
    description,
    ogTitle: title,
    ogDescription: description,
    ogType: 'article',
    ogUrl: canonicalUrl,
    ogImage: shareImage,
    ogImageAlt: 'Smoxy in-app community feed',
    twitterCard: 'summary_large_image',
    twitterTitle: title,
    twitterDescription: description,
    twitterImage: shareImage,
    robots: 'index,follow',
  })

  useHead({
    link: [
      { rel: 'canonical', href: canonicalUrl },
    ],
  })

 const openSmoxy = () => {
  const deepLink = "smoxy://live"
  const fallback = "https://apps.apple.com/app/apple-store/id1511487101?pt=117785216&ct=applefeat&mt=8" // optional

  window.location.href = deepLink

  setTimeout(() => {
    window.location.href = fallback
  }, 1200)
}

  </script>
  
